import { useEffect, useState } from 'react'
import { useAppSelector } from '../hooks/redux'
import { fetchPortalBookings, type PortalBooking } from '../services/guestPortalSupabase'

type GuestBookingRecord = PortalBooking

const formatCurrency = (amount: number) => `R${amount.toLocaleString()}`

const normalizeStatus = (value: unknown, fallback = 'pending') => String(value || fallback).toLowerCase()

const loadGuestBookingsFromStorage = (email?: string) => {
	try {
		const raw = localStorage.getItem('guestBookings')
		const parsed = raw ? JSON.parse(raw) : []
		const allBookings = Array.isArray(parsed) ? parsed : []

		return email
			? allBookings.filter((booking: GuestBookingRecord) => booking.guestEmail === email)
			: allBookings
	} catch (error) {
		console.error('Failed to load guest bookings:', error)
		return []
	}
}

const GuestDashboard = () => {
	const { user } = useAppSelector((state) => state.auth)
	const [bookings, setBookings] = useState<GuestBookingRecord[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')

	useEffect(() => {
		let cancelled = false

		const loadBookings = async () => {
			setLoading(true)
			setError('')

			if (!user?.email) {
				setBookings([])
				setLoading(false)
				return
			}

			try {
				const mapped = await fetchPortalBookings()

				if (!cancelled) {
					setBookings(mapped)
					localStorage.setItem('guestBookings', JSON.stringify(mapped))
				}
			} catch (loadError) {
				console.error('Failed to load guest bookings from Supabase:', loadError)
				if (!cancelled) {
					setError('Showing locally saved bookings because the live booking history could not be loaded.')
					setBookings(loadGuestBookingsFromStorage(user.email))
				}
			} finally {
				if (!cancelled) {
					setLoading(false)
				}
			}
		}

		void loadBookings()

		return () => {
			cancelled = true
		}
	}, [user?.email])

	const upcomingBookings = bookings.filter((booking) => {
		if (!booking.checkIn) return false
		return new Date(booking.checkIn) >= new Date(new Date().toDateString())
	})

	return (
		<div className="space-y-8">
			<section className="rounded-3xl border border-sky-100 bg-white/95 p-8 shadow-lg backdrop-blur">
				<div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
					<div>
						<p className="mb-2 text-sm font-semibold uppercase tracking-[0.22em] text-sky-600">Guest Dashboard</p>
						<h2 className="text-3xl font-bold text-slate-900">Welcome{user?.firstName ? `, ${user.firstName}` : ''}</h2>
						<p className="mt-2 text-slate-600">View your upcoming stays, payment status, and reservation history.</p>
					</div>
					<div className="grid gap-4 sm:grid-cols-2">
						<div className="rounded-2xl bg-sky-50 px-5 py-4 border border-sky-100">
							<div className="text-sm text-slate-500">Total Bookings</div>
							<div className="mt-1 text-2xl font-bold text-slate-900">{bookings.length}</div>
						</div>
						<div className="rounded-2xl bg-emerald-50 px-5 py-4 border border-emerald-100">
							<div className="text-sm text-slate-500">Upcoming Stays</div>
							<div className="mt-1 text-2xl font-bold text-slate-900">{upcomingBookings.length}</div>
						</div>
					</div>
				</div>
			</section>

			<section className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
				<div className="rounded-3xl border border-slate-200 bg-white/95 p-8 shadow-lg backdrop-blur">
					<div className="mb-6 flex items-center justify-between">
						<div>
							<h3 className="text-2xl font-bold text-slate-900">My Reservations</h3>
							<p className="text-slate-500">All bookings linked to your guest profile.</p>
						</div>
					</div>

					{loading ? (
						<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-500">
							Loading your booking history...
						</div>
					) : bookings.length === 0 ? (
						<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
							<div className="mb-3 text-5xl">🧳</div>
							<h4 className="text-xl font-semibold text-slate-900">No bookings found</h4>
							<p className="mt-2 text-slate-500">Your confirmed and pending reservations will appear here after payment.</p>
						</div>
					) : (
						<div className="space-y-4">
							{error && (
								<div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
									{error}
								</div>
							)}
							{bookings
								.slice()
								.sort((a, b) => new Date(b.bookedDate || 0).getTime() - new Date(a.bookedDate || 0).getTime())
								.map((booking) => {
									const bookingStatus = normalizeStatus(booking.bookingStatus || booking.status)
									const paymentStatus = normalizeStatus(booking.paymentStatus)
									const bookingBadge = bookingStatus === 'confirmed'
										? 'bg-emerald-100 text-emerald-800'
										: 'bg-amber-100 text-amber-800'
									const paymentBadge = paymentStatus === 'paid'
										? 'bg-sky-100 text-sky-800'
										: 'bg-amber-100 text-amber-800'

									return (
										<article key={booking.id} className="rounded-2xl border border-slate-200 p-6 transition-colors hover:border-sky-200 hover:bg-sky-50/40">
											<div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
												<div>
													<div className="flex flex-wrap items-center gap-2">
														<h4 className="text-xl font-bold text-slate-900">{booking.roomName || 'Marina Glen Stay'}</h4>
														<span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${bookingBadge}`}>
															{bookingStatus}
														</span>
														<span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${paymentBadge}`}>
															Payment {paymentStatus}
														</span>
													</div>
													<p className="mt-1 text-sm text-slate-500">Reference: {booking.confirmationNumber || 'Pending reference'}</p>
													<div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm text-slate-600">
														<div><strong>Check-in:</strong> {booking.checkIn ? new Date(booking.checkIn).toLocaleDateString() : 'N/A'}</div>
														<div><strong>Check-out:</strong> {booking.checkOut ? new Date(booking.checkOut).toLocaleDateString() : 'N/A'}</div>
														<div><strong>Guests:</strong> {booking.guests || 0}</div>
														<div><strong>Payment Method:</strong> {(booking.method || 'pending').toUpperCase()}</div>
													</div>
												</div>
												<div className="rounded-2xl bg-slate-900 px-5 py-4 text-white min-w-[160px]">
													<div className="text-xs uppercase tracking-[0.24em] text-slate-300">Total</div>
													<div className="mt-2 text-2xl font-bold">{formatCurrency(Number(booking.amount || 0))}</div>
												</div>
											</div>
										</article>
									)
								})}
						</div>
					)}
				</div>

				<div className="space-y-8">
					<section className="rounded-3xl border border-slate-200 bg-white/95 p-8 shadow-lg backdrop-blur">
						<h3 className="text-2xl font-bold text-slate-900">Guest Profile</h3>
						<div className="mt-6 space-y-4 text-sm text-slate-600">
							<div>
								<div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Name</div>
								<div className="mt-1 text-base font-medium text-slate-900">{user?.firstName || ''} {user?.lastName || ''}</div>
							</div>
							<div>
								<div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Email</div>
								<div className="mt-1 text-base font-medium text-slate-900">{user?.email || 'Not available'}</div>
							</div>
							<div>
								<div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Role</div>
								<div className="mt-1 text-base font-medium text-slate-900 capitalize">{user?.role || 'guest'}</div>
							</div>
						</div>
					</section>

					<section className="rounded-3xl border border-sky-100 bg-sky-50/90 p-8 shadow-lg">
						<h3 className="text-xl font-bold text-slate-900">Need help with a booking?</h3>
						<p className="mt-2 text-sm text-slate-600">If you paid by EFT, your reservation stays pending until the admin verifies payment and updates the booking.</p>
						<div className="mt-5 space-y-2 text-sm text-slate-700">
							<div><strong>Email:</strong> bookings@marinaglen.co.za</div>
							<div><strong>Phone:</strong> +27 39 312 1234</div>
						</div>
					</section>
				</div>
			</section>
		</div>
	)
}

export default GuestDashboard